import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Menu, X, ChevronDown, Sparkles, GraduationCap, BookOpen,
  Users, Compass, Target, Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { LOGO_URL } from "@/lib/siteData";

const programLinks = [
  {
    label: "Preschool Program",
    sub: "Play-based learning · Ages 2–5",
    to: "/programs/preschool",
    icon: BookOpen,
    color: "from-brand-green to-brand-green-deep",
    pill: "bg-brand-green-soft text-brand-green-deep",
    badge: "Ages 2–5",
  },
  {
    label: "Preschool Special Education",
    sub: "NYS 4410 · Integrated therapies",
    to: "/programs/special-education",
    icon: GraduationCap,
    color: "from-brand-purple to-brand-purple-deep",
    pill: "bg-brand-purple-soft text-brand-purple-deep",
    badge: "4410",
  },
];

const aboutLinks = [
  {
    label: "About",
    sub: "Our story, vision and team",
    to: "/about",
    icon: Users,
    color: "from-brand-purple to-brand-purple-deep",
    pill: "bg-brand-purple-soft text-brand-purple-deep",
    badge: "Who we are",
  },
  {
    label: "Our Approach",
    sub: "The four pillars guiding every classroom",
    to: "/approach",
    icon: Compass,
    color: "from-brand-green to-brand-green-deep",
    pill: "bg-brand-green-soft text-brand-green-deep",
    badge: "How we teach",
  },
  {
    label: "Our Goals",
    sub: "What we aim for, for every child",
    to: "/goals",
    icon: Target,
    color: "from-amber-500 to-amber-600",
    pill: "bg-amber-100 text-amber-800",
    badge: "Mission",
  },
  {
    label: "Philosophy",
    sub: "Our philosophy statement",
    to: "/philosophy",
    icon: Heart,
    color: "from-pink-500 to-rose-500",
    pill: "bg-rose-100 text-rose-700",
    badge: "Beliefs",
  },
];

const aboutPaths = aboutLinks.map((l) => l.to);
const programPaths = programLinks.map((l) => l.to);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProgramsOpen, setMobileProgramsOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const linkBase =
    "relative px-3.5 py-2 text-[14px] font-semibold text-gray-700 hover:text-brand-green-deep transition-colors rounded-full";
  const activeCls = "text-brand-green-deep bg-brand-green-soft/70";

  const programsActive = programPaths.some((p) => location.pathname.startsWith(p));
  const aboutActive = aboutPaths.includes(location.pathname);

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-3 sm:top-4 inset-x-0 z-50 px-3 sm:px-5"
    >
      <div
        className={`relative max-w-6xl mx-auto rounded-[28px] lg:rounded-full px-3 sm:px-4 py-2 flex items-center justify-between gap-3 transition-all duration-300
          bg-white/75 supports-[backdrop-filter]:bg-white/55 backdrop-blur-2xl
          ring-1 ring-black/[0.06]
          ${scrolled
            ? "shadow-[0_18px_50px_-22px_rgba(20,40,20,0.35)]"
            : "shadow-[0_10px_30px_-18px_rgba(20,40,20,0.25)]"
          }`}
      >
        {/* Soft gradient glow accent */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-[28px] lg:rounded-full overflow-hidden"
        >
          <div className="absolute -top-12 -left-10 h-32 w-32 rounded-full bg-brand-green-soft/60 blur-3xl" />
          <div className="absolute -bottom-12 -right-10 h-32 w-32 rounded-full bg-brand-purple-soft/70 blur-3xl" />
        </div>

        {/* Logo */}
        <Link
          to="/"
          data-testid="navbar-logo"
          className="relative flex items-center gap-2 group shrink-0 pl-1"
          aria-label="Innovators Prep — Home"
        >
          <img
            src={LOGO_URL}
            alt="Innovators Prep logo"
            className="h-12 md:h-14 w-auto object-contain"
          />
        </Link>

        {/* Desktop links */}
        <nav className="relative hidden lg:flex items-center gap-0.5">
          <NavLink
            to="/"
            data-testid="nav-link-home"
            className={({ isActive }) => `${linkBase} ${isActive ? activeCls : ""}`}
          >
            Home
          </NavLink>

          {/* Programs */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-programs-trigger"
                className={`${linkBase} flex items-center gap-1 outline-none data-[state=open]:text-brand-green-deep data-[state=open]:bg-brand-green-soft/70 ${programsActive ? activeCls : ""}`}
              >
                Programs <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="center"
              sideOffset={12}
              className="rounded-3xl p-3 border-brand-purple-soft shadow-2xl min-w-[480px] bg-white"
            >
              <div className="grid gap-2.5">
                {programLinks.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    data-testid={`nav-program-${p.to.split("/").pop()}`}
                    className="group flex items-start gap-4 rounded-2xl p-4 hover:bg-brand-cream transition-colors border border-transparent hover:border-gray-100"
                  >
                    <span className={`h-12 w-12 shrink-0 rounded-2xl bg-gradient-to-br ${p.color} text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform`}>
                      <p.icon className="h-5 w-5" />
                    </span>
                    <div className="flex-1">
                      <div className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${p.pill} mb-1`}>
                        {p.badge}
                      </div>
                      <div className="font-display text-[15px] font-bold text-gray-900 leading-tight">
                        {p.label}
                      </div>
                      <div className="text-[12.5px] text-gray-500 mt-0.5">{p.sub}</div>
                    </div>
                    <ChevronDown className="h-4 w-4 -rotate-90 text-gray-400 group-hover:text-brand-green-deep group-hover:translate-x-0.5 transition-all mt-3" />
                  </Link>
                ))}
              </div>
              <div className="mt-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-brand-green-soft to-brand-purple-soft flex items-center gap-3">
                <Sparkles className="h-4 w-4 text-brand-purple-deep shrink-0" />
                <span className="text-[12.5px] text-gray-700">
                  Curious about both? <Link to="/approach" className="font-bold text-brand-purple-deep underline underline-offset-2">See our approach →</Link>
                </span>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* About Us */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-aboutus-trigger"
                className={`${linkBase} flex items-center gap-1 outline-none data-[state=open]:text-brand-green-deep data-[state=open]:bg-brand-green-soft/70 ${aboutActive ? activeCls : ""}`}
              >
                About Us <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="center"
              sideOffset={12}
              className="rounded-3xl p-3 border-brand-purple-soft shadow-2xl min-w-[520px] bg-white"
            >
              <div className="grid grid-cols-2 gap-2.5">
                {aboutLinks.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    data-testid={`nav-aboutus-${p.to.replace(/^\//, "")}`}
                    className="group flex items-start gap-3 rounded-2xl p-3.5 hover:bg-brand-cream transition-colors border border-transparent hover:border-gray-100"
                  >
                    <span className={`h-11 w-11 shrink-0 rounded-2xl bg-gradient-to-br ${p.color} text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform`}>
                      <p.icon className="h-5 w-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${p.pill} mb-1`}>
                        {p.badge}
                      </div>
                      <div className="font-display text-[14.5px] font-bold text-gray-900 leading-tight">
                        {p.label}
                      </div>
                      <div className="text-[12px] text-gray-500 mt-0.5 leading-snug">{p.sub}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <NavLink
            to="/term-dates"
            data-testid="nav-link-term-dates"
            className={({ isActive }) => `${linkBase} ${isActive ? activeCls : ""}`}
          >
            Term Dates & Planning
          </NavLink>
        </nav>

        {/* CTA + Mobile menu button */}
        <div className="relative flex items-center gap-2">
          <Button
            asChild
            data-testid="nav-contact-cta"
            className="hidden sm:inline-flex bg-gradient-to-r from-brand-green to-brand-green-deep hover:from-brand-green-deep hover:to-brand-green text-white rounded-full px-5 py-2 font-semibold shadow-[0_8px_24px_-8px_rgba(22,163,74,0.55)] hover:shadow-[0_12px_28px_-8px_rgba(22,163,74,0.65)] hover:-translate-y-0.5 transition-all ring-1 ring-white/20"
          >
            <Link to="/contact">Contact Us</Link>
          </Button>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-full bg-white/70 hover:bg-brand-green-soft ring-1 ring-black/5 transition-colors"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden max-w-6xl mx-auto mt-2 rounded-3xl bg-white/90 supports-[backdrop-filter]:bg-white/80 backdrop-blur-2xl ring-1 ring-black/[0.06] shadow-[0_18px_50px_-22px_rgba(20,40,20,0.3)] max-h-[80vh] overflow-y-auto"
        >
          <div className="px-5 py-4 flex flex-col gap-1">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `px-3 py-3 rounded-xl font-semibold ${
                  isActive ? "bg-brand-green-soft text-brand-green-deep" : "text-gray-700"
                }`
              }
            >
              Home
            </NavLink>

            <button
              onClick={() => setMobileProgramsOpen((v) => !v)}
              className="px-3 py-3 rounded-xl font-semibold text-gray-700 flex items-center justify-between"
              data-testid="mobile-programs-toggle"
            >
              Programs
              <ChevronDown
                className={`h-4 w-4 transition-transform ${mobileProgramsOpen ? "rotate-180" : ""}`}
              />
            </button>
            {mobileProgramsOpen && (
              <div className="ml-2 mb-2 flex flex-col gap-2">
                {programLinks.map((p) => (
                  <NavLink
                    key={p.to}
                    to={p.to}
                    className="flex items-start gap-3 px-3 py-3 rounded-2xl bg-brand-cream"
                  >
                    <span className={`h-10 w-10 shrink-0 rounded-xl bg-gradient-to-br ${p.color} text-white flex items-center justify-center`}>
                      <p.icon className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="font-display text-[14px] font-bold text-gray-900">{p.label}</div>
                      <div className="text-[12px] text-gray-500">{p.sub}</div>
                    </div>
                  </NavLink>
                ))}
              </div>
            )}

            <button
              onClick={() => setMobileAboutOpen((v) => !v)}
              className="px-3 py-3 rounded-xl font-semibold text-gray-700 flex items-center justify-between"
              data-testid="mobile-aboutus-toggle"
            >
              About Us
              <ChevronDown
                className={`h-4 w-4 transition-transform ${mobileAboutOpen ? "rotate-180" : ""}`}
              />
            </button>
            {mobileAboutOpen && (
              <div className="ml-2 mb-2 flex flex-col gap-2">
                {aboutLinks.map((p) => (
                  <NavLink
                    key={p.to}
                    to={p.to}
                    className="flex items-start gap-3 px-3 py-3 rounded-2xl bg-brand-cream"
                  >
                    <span className={`h-10 w-10 shrink-0 rounded-xl bg-gradient-to-br ${p.color} text-white flex items-center justify-center`}>
                      <p.icon className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="font-display text-[14px] font-bold text-gray-900">{p.label}</div>
                      <div className="text-[12px] text-gray-500">{p.sub}</div>
                    </div>
                  </NavLink>
                ))}
              </div>
            )}

            <NavLink
              to="/term-dates"
              className={({ isActive }) =>
                `px-3 py-3 rounded-xl font-semibold ${
                  isActive ? "bg-brand-green-soft text-brand-green-deep" : "text-gray-700"
                }`
              }
            >
              Term Dates & Planning
            </NavLink>

            <Button
              asChild
              className="mt-2 bg-brand-green hover:bg-brand-green-deep text-white rounded-full font-semibold"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
